import { useState } from 'react';
import {
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Copy,
  Webhook,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSyncIntegration, useDeleteIntegration } from '../../hooks/useIntegrations';
import { integrationsService } from '../../services/integrations.service';
import { IntegrationSyncLogs } from './IntegrationSyncLogs';

interface IntegrationCardProps {
  integration: any;
  onEdit: () => void;
}

export function IntegrationCard({ integration, onEdit }: IntegrationCardProps) {
  const [showLogs, setShowLogs] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const syncMutation = useSyncIntegration();
  const deleteMutation = useDeleteIntegration();

  function handleDelete() {
    if (!confirm(`Tem certeza que deseja excluir "${integration.name}"?`)) {
      return;
    }
    deleteMutation.mutate(integration.id, {
      onSuccess: () => toast.success('Integração excluída'),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Erro ao excluir'),
    });
  }

  async function handleCopyWebhookUrl() {
    try {
      if (!webhookUrl) {
        const data = await integrationsService.getWebhookUrl(integration.id);
        setWebhookUrl(data.url);
        await navigator.clipboard.writeText(data.url);
      } else {
        await navigator.clipboard.writeText(webhookUrl);
      }
      toast.success('Webhook URL copiada!');
    } catch {
      toast.error('Erro ao copiar webhook URL');
    }
  }

  function handleSync() {
    syncMutation.mutate(integration.id, {
      onSuccess: () => toast.success('Sincronização concluída!'),
      onError: (err: any) => toast.error(err.response?.data?.error || 'Erro na sincronização'),
    });
  }

  function formatDate(date: string | null) {
    if (!date) {
      return 'Nunca';
    }
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const statusColors: Record<string, string> = {
    SUCCESS: 'text-green-600',
    ERROR: 'text-red-600',
    RUNNING: 'text-blue-600',
  };

  function statusIcon(status: string | null) {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'ERROR':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'RUNNING':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-secondary-400" />;
    }
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-secondary-200 p-5 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-2.5 h-2.5 rounded-full ${integration.active ? 'bg-green-500' : 'bg-secondary-300'}`}
            />
            <div>
              <h3 className="font-semibold text-secondary-900">{integration.name}</h3>
              <p className="text-xs text-secondary-500">
                Tipo: {integration.type} · Produto: {integration.product?.name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {integration.active && (
              <button
                onClick={handleSync}
                disabled={syncMutation.isPending || integration.lastSyncStatus === 'RUNNING'}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
              >
                {syncMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Sync
              </button>
            )}
            <button
              onClick={onEdit}
              className="p-2 rounded-lg hover:bg-secondary-100 text-secondary-500"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="p-2 rounded-lg hover:bg-red-50 text-secondary-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="text-sm text-secondary-600 space-y-1">
          <p>
            Vendedor padrão:{' '}
            <span className="font-medium">{integration.defaultAssignedUser?.name}</span>
          </p>
          <p>Intervalo: a cada {integration.syncIntervalMinutes} min</p>
        </div>

        <div className="mt-3 pt-3 border-t border-secondary-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {statusIcon(integration.lastSyncStatus)}
            <span>
              Última sync: {formatDate(integration.lastSyncAt)}
              {integration.lastSyncStatus && (
                <span className={`ml-1 ${statusColors[integration.lastSyncStatus] || ''}`}>
                  {integration.lastSyncStatus}
                </span>
              )}
            </span>
          </div>

          {integration.lastSyncAt && (
            <div className="flex items-center gap-3 text-xs text-secondary-500">
              {integration.lastSyncLeadsCreated !== null &&
                integration.lastSyncLeadsCreated !== undefined && (
                  <span>Criados: {integration.lastSyncLeadsCreated}</span>
                )}
              {integration.lastSyncLeadsUpdated !== null &&
                integration.lastSyncLeadsUpdated !== undefined && (
                  <span>Atualizados: {integration.lastSyncLeadsUpdated}</span>
                )}
            </div>
          )}
        </div>

        {integration.lastSyncError && (
          <p className="mt-2 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-1.5">
            {integration.lastSyncError}
          </p>
        )}

        <div className="mt-2 flex items-center gap-3">
          <button
            onClick={() => setShowLogs(true)}
            className="text-xs text-primary-600 hover:underline"
          >
            Ver histórico de syncs
          </button>
          <button
            onClick={handleCopyWebhookUrl}
            className="flex items-center gap-1 text-xs text-secondary-500 hover:text-primary-600"
          >
            <Webhook className="w-3 h-3" />
            Copiar Webhook URL
          </button>
        </div>
        {webhookUrl && (
          <div className="mt-2 flex items-center gap-2 bg-secondary-50 rounded-lg px-3 py-2">
            <code className="text-xs text-secondary-600 flex-1 truncate">{webhookUrl}</code>
            <button
              onClick={() =>
                navigator.clipboard.writeText(webhookUrl).then(() => toast.success('Copiada!'))
              }
              className="text-secondary-400 hover:text-primary-600"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {showLogs && (
        <IntegrationSyncLogs
          integrationId={integration.id}
          integrationName={integration.name}
          onClose={() => setShowLogs(false)}
        />
      )}
    </>
  );
}
